import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, ChevronRight, ChevronDown, Server, Terminal,
  Package, RefreshCw, Users, Shield, Archive, Settings,
  Zap, AlertTriangle, CheckCircle, Copy, Check, ExternalLink,
  BookOpen, Play, HardDrive, Globe, Lock, Cpu
} from 'lucide-react';

// ─── CODE BLOCK ───────────────────────────────────────────────
const CodeBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-3">
      <pre className="bg-gray-950 border border-gray-800 rounded-xl px-4 py-3 text-green-400 font-mono text-sm overflow-x-auto">
        <code>{children}</code>
      </pre>
      <button
        onClick={copy}
        className="absolute top-2.5 right-2.5 p-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
      </button>
    </div>
  );
};

// ─── CALLOUT ──────────────────────────────────────────────────
const Callout = ({ type = 'info', children }) => {
  const styles = {
    info:    { bg: 'bg-blue-500/10 border-blue-500/20',   icon: <Zap className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />,          text: 'text-blue-300' },
    warning: { bg: 'bg-yellow-500/10 border-yellow-500/20', icon: <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />, text: 'text-yellow-300' },
    success: { bg: 'bg-green-500/10 border-green-500/20',  icon: <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />,    text: 'text-green-300' },
    danger:  { bg: 'bg-red-500/10 border-red-500/20',     icon: <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />,     text: 'text-red-300' },
  };
  const s = styles[type];
  return (
    <div className={`flex gap-2.5 border rounded-xl p-4 my-3 ${s.bg}`}>
      {s.icon}
      <p className={`text-sm leading-relaxed ${s.text}`}>{children}</p>
    </div>
  );
};

// ─── STEP ─────────────────────────────────────────────────────
const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-6">
    <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-gray-950 font-black text-sm flex-shrink-0 mt-0.5">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-bold text-white mb-2">{title}</h4>
      <div className="text-gray-400 text-sm leading-relaxed">{children}</div>
    </div>
  </div>
);

// ─── DOCS CONTENT ─────────────────────────────────────────────
const DOCS = [
  {
    id: 'getting-started',
    icon: Zap,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    title: 'Getting Started',
    desc: 'Everything you need to launch your first server',
    articles: [
      {
        id: 'first-server',
        title: 'Launching your first server',
        tags: ['beginner', 'setup'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">After purchasing a plan your server will be set up automatically within 30 seconds. Here's how to get connected and start playing.</p>

            <Step number={1} title="Find your server details">
              Go to your <strong className="text-white">Dashboard</strong> and click on your server. You'll see the IP address and port (e.g. <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">play.plarixmc.com:25565</code>).
            </Step>
            <Step number={2} title="Open Minecraft Java Edition">
              Launch Minecraft and click <strong className="text-white">Multiplayer</strong> → <strong className="text-white">Add Server</strong>. Enter your server address and click <strong className="text-white">Done</strong>.
            </Step>
            <Step number={3} title="Start your server">
              If your server shows as <span className="text-yellow-400">Offline</span>, go to your dashboard and click the <strong className="text-white">Start</strong> button. It takes about 30 seconds to boot.
            </Step>
            <Step number={4} title="Join and play">
              Select your server from the list and click <strong className="text-white">Join Server</strong>. You're in!
            </Step>

            <Callout type="success">Your server runs Paper by default — the best performance-optimised Minecraft server software, fully compatible with all Bukkit/Spigot plugins.</Callout>
          </div>
        ),
      },
      {
        id: 'dashboard-overview',
        title: 'Dashboard overview',
        tags: ['beginner'],
        content: (
          <div>
            <p className="text-gray-400 mb-6 leading-relaxed">Your dashboard is the control centre for all your servers. Here's what everything does.</p>
            {[
              { icon: '▶️', label: 'Start', desc: 'Powers on your server. Takes ~30 seconds to fully boot.' },
              { icon: '⏹️', label: 'Stop', desc: 'Gracefully shuts down the server, saving all player data and chunks.' },
              { icon: '🔄', label: 'Restart', desc: 'Stops and starts the server. Use this after installing plugins or changing settings.' },
              { icon: '⚙️', label: 'Manage', desc: 'Opens the full server management panel with files, backups, players and settings.' },
              { icon: '🖥️', label: 'Console', desc: 'Live terminal — run commands, view logs, debug issues in real time.' },
            ].map(({ icon, label, desc }) => (
              <div key={label} className="flex gap-3 mb-4 bg-gray-800/50 rounded-xl p-4">
                <span className="text-xl">{icon}</span>
                <div>
                  <div className="font-semibold text-white">{label}</div>
                  <div className="text-gray-400 text-sm mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        ),
      },
    ],
  },
  {
    id: 'plugins',
    icon: Package,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    title: 'Plugins',
    desc: 'Install and manage Bukkit, Spigot and Paper plugins',
    articles: [
      {
        id: 'install-plugins',
        title: 'How to install plugins',
        tags: ['plugins', 'popular'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">Plugins extend your server with new features — from economy systems to mini-games. You can install as many as you like.</p>

            <Callout type="info">Plugins only work on Paper, Spigot and Bukkit servers. If you're running Vanilla or Fabric, you need mods instead.</Callout>

            <h3 className="text-white font-bold text-lg mt-6 mb-4">Method 1 — File Manager (easiest)</h3>
            <Step number={1} title="Download the plugin">
              Go to <a href="https://www.spigotmc.org/resources/" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">SpigotMC.org</a> or <a href="https://hangar.papermc.io/" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">Hangar (Paper)</a> and download the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">.jar</code> file for your plugin.
            </Step>
            <Step number={2} title="Open File Manager">
              Go to <strong className="text-white">Dashboard → Manage → Files</strong> and open the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">plugins/</code> folder.
            </Step>
            <Step number={3} title="Upload the .jar file">
              Drag and drop your <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">.jar</code> file into the plugins folder.
            </Step>
            <Step number={4} title="Restart your server">
              Go back to your dashboard and click <strong className="text-white">Restart</strong>. The plugin will load automatically.
            </Step>

            <h3 className="text-white font-bold text-lg mt-6 mb-4">Method 2 — Console command</h3>
            <p className="text-gray-400 text-sm mb-3">If your server is running, open the Console and type:</p>
            <CodeBlock>plugman load PluginName</CodeBlock>
            <Callout type="warning">Not all plugins support hot-loading. A full restart is always more reliable.</Callout>

            <h3 className="text-white font-bold text-lg mt-6 mb-4">Recommended plugins to start with</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { name: 'EssentialsX', desc: 'Warps, homes, economy basics, /tp and more', link: 'https://essentialsx.net' },
                { name: 'LuckPerms', desc: 'Powerful permission management and rank system', link: 'https://luckperms.net' },
                { name: 'WorldEdit', desc: 'In-game map editor for builders', link: 'https://enginehub.org/worldedit' },
                { name: 'Vault', desc: 'Economy API — required by many other plugins', link: 'https://github.com/MilkBowl/Vault' },
                { name: 'CoreProtect', desc: 'Block logging and rollback — essential for grief protection', link: 'https://www.spigotmc.org/resources/coreprotect.8631/' },
                { name: 'GriefPrevention', desc: 'Land claiming system for players', link: 'https://github.com/TechFortress/GriefPrevention' },
              ].map(({ name, desc, link }) => (
                <a key={name} href={link} target="_blank" rel="noreferrer" className="flex items-start gap-3 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl p-3.5 transition-colors group">
                  <Package className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white text-sm group-hover:text-purple-400 transition-colors flex items-center gap-1">
                      {name} <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="text-gray-500 text-xs mt-0.5">{desc}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: 'configure-plugins',
        title: 'Configuring plugins',
        tags: ['plugins'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">Most plugins generate a config file after their first run. Here's how to edit them.</p>
            <Step number={1} title="Restart your server once">
              After installing a plugin, restart so it generates its default config files inside the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">plugins/PluginName/</code> folder.
            </Step>
            <Step number={2} title="Open the config file">
              Go to <strong className="text-white">Manage → Files</strong> → open <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">plugins/</code> → find your plugin folder → click <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">config.yml</code>.
            </Step>
            <Step number={3} title="Edit and save">
              Make your changes in the editor and click <strong className="text-white">Save</strong>.
            </Step>
            <Step number={4} title="Reload the plugin">
              In the Console, type:
              <CodeBlock>/pluginname reload</CodeBlock>
              Or just restart the server.
            </Step>
            <Callout type="warning">YAML files are indent-sensitive. Use spaces, never tabs. If the server fails to start after editing, check your indentation.</Callout>
          </div>
        ),
      },
    ],
  },
  {
    id: 'mods',
    icon: Cpu,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    title: 'Mods',
    desc: 'Install Forge and Fabric mods on your server',
    articles: [
      {
        id: 'install-mods',
        title: 'How to install mods (Forge / Fabric)',
        tags: ['mods'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">Mods work differently from plugins — both the server <strong className="text-white">and the client</strong> need to have the same mods installed.</p>

            <Callout type="info">If you just want to add features without requiring players to install anything, use <strong>plugins</strong> on a Paper server instead.</Callout>

            <h3 className="text-white font-bold text-lg mt-6 mb-4">Setting up a Forge server</h3>
            <Step number={1} title="Switch to Forge">
              Go to <strong className="text-white">Manage → Settings</strong>, change Server Type to <strong className="text-white">Forge</strong> and pick your Minecraft version. Save and restart.
            </Step>
            <Step number={2} title="Download mods">
              Download <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">.jar</code> mod files from <a href="https://www.curseforge.com/minecraft/mc-mods" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">CurseForge</a> or <a href="https://modrinth.com" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">Modrinth</a>. Make sure the mod version matches your Minecraft and Forge version.
            </Step>
            <Step number={3} title="Upload to mods folder">
              Go to <strong className="text-white">Manage → Files</strong> and upload your mod <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">.jar</code> files into the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">mods/</code> folder.
            </Step>
            <Step number={4} title="Install same mods on your client">
              Your players need to install the same mods using the <a href="https://files.minecraftforge.net" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">Forge launcher</a> or a modpack launcher like <a href="https://www.curseforge.com/download/app" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">CurseForge App</a>.
            </Step>
            <Step number={5} title="Restart and connect">
              Restart your server and connect using the Forge profile in your Minecraft launcher.
            </Step>

            <h3 className="text-white font-bold text-lg mt-6 mb-4">Fabric server setup</h3>
            <p className="text-gray-400 text-sm mb-3">Fabric is lighter and faster than Forge. Follow the same steps but:</p>
            <ul className="space-y-2 text-sm text-gray-400 ml-4">
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />Set Server Type to <strong className="text-white">Fabric</strong></li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />Download Fabric mods from <a href="https://modrinth.com" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">Modrinth</a></li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />Install <a href="https://fabricmc.net/use/installer/" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">Fabric Loader</a> on your client</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />Add <a href="https://modrinth.com/mod/fabric-api" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">Fabric API</a> to your mods folder — most mods need it</li>
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    id: 'version',
    icon: RefreshCw,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    title: 'Version & Software',
    desc: 'Change Minecraft version, server type and Java',
    articles: [
      {
        id: 'change-version',
        title: 'Changing your Minecraft version',
        tags: ['version', 'popular'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">You can change your server's Minecraft version at any time from the Settings tab.</p>

            <Callout type="danger">Downgrading versions (e.g. 1.20 → 1.19) can corrupt your world. Always create a backup first!</Callout>

            <Step number={1} title="Back up your world">
              Go to <strong className="text-white">Manage → Backups</strong> and click <strong className="text-white">Create Backup</strong>. Wait for it to complete.
            </Step>
            <Step number={2} title="Open Settings">
              Go to <strong className="text-white">Manage → Settings</strong>.
            </Step>
            <Step number={3} title="Select your new version">
              Choose your Minecraft version from the dropdown. Available versions: 1.8.9, 1.12.2, 1.16.5, 1.17.1, 1.18.2, 1.19.4, 1.20.1, 1.20.2, 1.20.4.
            </Step>
            <Step number={4} title="Save and restart">
              Click <strong className="text-white">Save changes</strong>. Your server will restart and download the new version automatically.
            </Step>

            <h3 className="text-white font-bold text-lg mt-6 mb-3">Java version guide</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400 text-left">
                    <th className="pb-2 font-medium">Minecraft version</th>
                    <th className="pb-2 font-medium">Java version</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50 text-gray-300">
                  {[['1.8 – 1.12', 'Java 8'],['1.13 – 1.16', 'Java 11'],['1.17 – 1.20', 'Java 17 (recommended)'],['1.20.5+', 'Java 21']].map(([mc, java]) => (
                    <tr key={mc}>
                      <td className="py-2 font-mono text-xs">{mc}</td>
                      <td className="py-2">{java}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
      },
      {
        id: 'server-types',
        title: 'Choosing the right server software',
        tags: ['version'],
        content: (
          <div>
            <p className="text-gray-400 mb-6 leading-relaxed">The server software you choose affects performance, plugin/mod compatibility and features.</p>
            {[
              { name: 'Paper', badge: 'Recommended', color: 'border-green-500/30', desc: 'The best choice for most servers. Dramatically better performance than Vanilla, supports all Bukkit/Spigot plugins, and has tons of extra configuration options. Start here unless you have a specific reason not to.', pros: ['Best performance', 'Plugin support', 'Active development', 'Great community'], cons: ['Slightly different from Vanilla behaviour'] },
              { name: 'Vanilla', badge: null, color: 'border-gray-700', desc: 'The official Mojang server. No plugins or mods. Best for a pure, unmodified Minecraft experience — but has poor performance at scale.', pros: ['True to original game', 'No compatibility issues'], cons: ['No plugin support', 'Poor performance', 'Limited config options'] },
              { name: 'Forge', badge: null, color: 'border-gray-700', desc: 'The standard for heavy modpacks. Supports thousands of mods but requires players to install matching mods too. Best for modded survival or RPG servers.', pros: ['Huge mod ecosystem', 'Modpacks support'], cons: ['Slower performance', 'Clients need matching mods', 'Updates can be slow'] },
              { name: 'Fabric', badge: null, color: 'border-gray-700', desc: 'A lightweight, modern mod loader. Faster than Forge and quicker to update. Growing mod ecosystem. Great for technical/performance mods.', pros: ['Fast & lightweight', 'Quick updates', 'Modern API'], cons: ['Smaller mod ecosystem than Forge', 'Clients need matching mods'] },
            ].map(({ name, badge, color, desc, pros, cons }) => (
              <div key={name} className={`border ${color} rounded-2xl p-5 mb-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-bold text-white text-lg">{name}</h3>
                  {badge && <span className="text-xs bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-full">{badge}</span>}
                </div>
                <p className="text-gray-400 text-sm mb-3">{desc}</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500 font-medium mb-1.5">✅ Pros</div>
                    {pros.map(p => <div key={p} className="text-gray-300 mb-1">{p}</div>)}
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium mb-1.5">❌ Cons</div>
                    {cons.map(c => <div key={c} className="text-gray-300 mb-1">{c}</div>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ),
      },
    ],
  },
  {
    id: 'console',
    icon: Terminal,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    title: 'Console & Commands',
    desc: 'Run commands and read logs from your browser',
    articles: [
      {
        id: 'using-console',
        title: 'Using the server console',
        tags: ['console', 'popular'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">The console lets you run commands directly on your server without needing to be in-game. Go to <strong className="text-white">Dashboard → Console icon</strong>.</p>

            <Step number={1} title="Connect to console">
              Click the green <strong className="text-white">Connect</strong> button. You'll see live server logs streaming in.
            </Step>
            <Step number={2} title="Type a command">
              Click the input box at the bottom, type your command (without the <code className="bg-gray-800 px-1.5 py-0.5 rounded text-green-400 font-mono text-xs">/</code>) and press Enter.
            </Step>

            <Callout type="info">In the server console you don't need the leading slash. Type <code>op Steve</code> not <code>/op Steve</code>.</Callout>

            <h3 className="text-white font-bold text-lg mt-6 mb-4">Essential commands</h3>
            <div className="space-y-2">
              {[
                ['op <player>', 'Give a player operator (admin) powers'],
                ['deop <player>', 'Remove operator powers from a player'],
                ['whitelist on', 'Enable whitelist — only listed players can join'],
                ['whitelist off', 'Disable whitelist — anyone can join'],
                ['whitelist add <player>', 'Add a player to the whitelist'],
                ['whitelist remove <player>', 'Remove a player from the whitelist'],
                ['ban <player>', 'Ban a player from the server'],
                ['pardon <player>', 'Unban a player'],
                ['ban-ip <ip>', 'Ban an IP address'],
                ['kick <player> [reason]', 'Kick a player with optional reason'],
                ['say <message>', 'Broadcast a message to all players'],
                ['time set day', 'Set the time to daytime'],
                ['time set night', 'Set the time to night'],
                ['weather clear', 'Clear the weather'],
                ['gamerule keepInventory true', 'Players keep items on death'],
                ['gamerule doDaylightCycle false', 'Freeze the time of day'],
                ['save-all', 'Force-save all chunks and player data'],
                ['stop', 'Gracefully shut down the server'],
              ].map(([cmd, desc]) => (
                <div key={cmd} className="flex gap-3 bg-gray-800/40 rounded-xl px-4 py-2.5">
                  <code className="text-green-400 font-mono text-xs w-56 flex-shrink-0">{cmd}</code>
                  <span className="text-gray-400 text-xs">{desc}</span>
                </div>
              ))}
            </div>

            <h3 className="text-white font-bold text-lg mt-6 mb-3">Reading logs</h3>
            <p className="text-gray-400 text-sm mb-3">Log lines are colour-coded to help you spot issues quickly:</p>
            <div className="space-y-2 text-sm">
              <div className="flex gap-2"><span className="text-gray-300 font-mono text-xs w-16">[INFO]</span><span className="text-gray-400">Normal server activity — players joining, chunks loading</span></div>
              <div className="flex gap-2"><span className="text-yellow-400 font-mono text-xs w-16">[WARN]</span><span className="text-gray-400">Non-critical issues — usually plugin compatibility warnings</span></div>
              <div className="flex gap-2"><span className="text-red-400 font-mono text-xs w-16">[ERROR]</span><span className="text-gray-400">Something went wrong — plugin crash, config error, out of memory</span></div>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'players',
    icon: Users,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    title: 'Player Management',
    desc: 'Whitelist, ops, bans and permissions',
    articles: [
      {
        id: 'whitelist',
        title: 'Setting up a whitelist',
        tags: ['players', 'security'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">A whitelist restricts your server to approved players only — great for private servers or communities.</p>

            <h3 className="text-white font-bold mb-3">Via the Players panel</h3>
            <Step number={1} title="Open Player Management">Go to <strong className="text-white">Manage → Players → Whitelist tab</strong>.</Step>
            <Step number={2} title="Add players">Type a player's username and click <strong className="text-white">Add</strong>.</Step>
            <Step number={3} title="Enable the whitelist">In the Console, type: <CodeBlock>whitelist on</CodeBlock></Step>

            <h3 className="text-white font-bold mb-3 mt-6">Via Console</h3>
            <CodeBlock>whitelist on</CodeBlock>
            <CodeBlock>whitelist add PlayerName</CodeBlock>
            <CodeBlock>whitelist remove PlayerName</CodeBlock>
            <CodeBlock>whitelist list</CodeBlock>

            <Callout type="warning">Players already online won't be kicked when you enable the whitelist. Restart the server to enforce it immediately.</Callout>
          </div>
        ),
      },
      {
        id: 'permissions',
        title: 'Setting up permissions with LuckPerms',
        tags: ['players', 'permissions'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">LuckPerms is the best permissions plugin. It lets you create ranks (groups) and control exactly what each rank can do.</p>

            <Callout type="info">Install LuckPerms first via the <strong>File Manager</strong> → plugins folder. Download from <a href="https://luckperms.net" target="_blank" rel="noreferrer" className="text-green-400 hover:underline">luckperms.net</a>.</Callout>

            <h3 className="text-white font-bold mt-5 mb-3">Create a rank</h3>
            <CodeBlock>lp creategroup moderator</CodeBlock>

            <h3 className="text-white font-bold mt-5 mb-3">Give a rank a permission</h3>
            <CodeBlock>lp group moderator permission set essentials.kick true</CodeBlock>

            <h3 className="text-white font-bold mt-5 mb-3">Assign a player to a rank</h3>
            <CodeBlock>lp user PlayerName parent set moderator</CodeBlock>

            <h3 className="text-white font-bold mt-5 mb-3">Common rank setup</h3>
            <div className="space-y-2 text-sm text-gray-400">
              {[['Default', 'All players automatically — set basic permissions here'],['VIP', 'Paying members or donors — extra features'],['Moderator', 'Can kick/ban/mute but not access server files'],['Admin', 'Full access — only for trusted staff']].map(([rank, desc]) => (
                <div key={rank} className="flex gap-3 bg-gray-800/40 rounded-xl px-4 py-2.5">
                  <span className="text-white font-semibold w-24 flex-shrink-0">{rank}</span>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'backups',
    icon: Archive,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    title: 'Backups',
    desc: 'Create, restore and download your world backups',
    articles: [
      {
        id: 'manage-backups',
        title: 'Creating and restoring backups',
        tags: ['backups', 'popular'],
        content: (
          <div>
            <p className="text-gray-400 mb-4 leading-relaxed">Backups save a complete snapshot of your server — world, plugins, config files, everything. Your plan includes 2 backup slots kept for 7 days.</p>

            <h3 className="text-white font-bold mb-4">Creating a backup</h3>
            <Step number={1} title="Go to Backups">Navigate to <strong className="text-white">Manage → Backups</strong>.</Step>
            <Step number={2} title="Name your backup">Enter a name like "Before 1.20 update" or leave blank for an auto-name.</Step>
            <Step number={3} title="Click Create">The backup runs in the background — your server stays online. Large worlds may take a few minutes.</Step>

            <h3 className="text-white font-bold mb-4 mt-6">Restoring a backup</h3>
            <Callout type="danger">Restoring a backup will overwrite your current world. This cannot be undone. Create a backup of your current state first if needed.</Callout>
            <Step number={1} title="Find your backup">In the Backups tab, find the backup you want to restore.</Step>
            <Step number={2} title="Click Restore">Click the <strong className="text-white">Restore</strong> button and confirm the warning.</Step>
            <Step number={3} title="Wait for restart">Your server will restart automatically. It may take 1–2 minutes to fully restore.</Step>

            <h3 className="text-white font-bold mb-3 mt-6">When should I back up?</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              {['Before updating to a new Minecraft version','Before installing new plugins or mods','Before running commands that affect the world (e.g. WorldEdit operations)','Before letting a new admin on your server','Weekly as a routine safety measure'].map(item => (
                <li key={item} className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />{item}</li>
              ))}
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    id: 'security',
    icon: Shield,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    title: 'Security',
    desc: 'Protect your server from griefers and attacks',
    articles: [
      {
        id: 'server-security',
        title: 'Securing your server',
        tags: ['security'],
        content: (
          <div>
            <p className="text-gray-400 mb-6 leading-relaxed">A well-secured server keeps griefers out and protects your community. Here's a complete security checklist.</p>

            {[
              { title: 'Enable online mode', desc: 'Online mode verifies players have a legitimate Minecraft account. Never disable it unless running a Bedrock bridge.', cmd: 'Set online-mode=true in server.properties', type: 'success' },
              { title: 'Set a strong RCON password', desc: 'RCON allows remote command execution. If enabled, use a strong random password or disable it entirely.', cmd: 'Set rcon.password=<strong random password> in server.properties', type: 'warning' },
              { title: 'Use a whitelist for private servers', desc: 'If your server is for friends only, enable the whitelist so strangers can\'t join.', cmd: 'whitelist on', type: 'info' },
              { title: 'Install CoreProtect', desc: 'Logs every block placed and broken. If someone griefs your server you can roll back their changes instantly.', cmd: null, type: 'info' },
              { title: 'Limit operator powers', desc: 'Only give OP to players you completely trust. Consider using LuckPerms instead of /op for granular control.', cmd: null, type: 'warning' },
              { title: 'Keep plugins updated', desc: 'Outdated plugins can have security vulnerabilities. Regularly update to the latest versions.', cmd: null, type: 'info' },
            ].map(({ title, desc, cmd, type }) => (
              <div key={title} className="mb-5">
                <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />{title}
                </h3>
                <p className="text-gray-400 text-sm mb-2">{desc}</p>
                {cmd && <CodeBlock>{cmd}</CodeBlock>}
              </div>
            ))}
          </div>
        ),
      },
    ],
  },
  {
    id: 'performance',
    icon: Zap,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    title: 'Performance',
    desc: 'Optimise your server for more players and less lag',
    articles: [
      {
        id: 'reduce-lag',
        title: 'Reducing lag and improving performance',
        tags: ['performance', 'popular'],
        content: (
          <div>
            <p className="text-gray-400 mb-6 leading-relaxed">Lag comes in two forms: <strong className="text-white">TPS lag</strong> (server-side, affects gameplay) and <strong className="text-white">ping lag</strong> (network, affects connection). Here's how to fix both.</p>

            <h3 className="text-white font-bold mb-4">Check your TPS</h3>
            <p className="text-gray-400 text-sm mb-2">TPS = Ticks Per Second. Target is 20 TPS. Below 18 means lag. In Console:</p>
            <CodeBlock>tps</CodeBlock>

            <h3 className="text-white font-bold mb-4 mt-6">Key server.properties settings</h3>
            <p className="text-gray-400 text-sm mb-3">Edit via <strong className="text-white">Manage → Files → server.properties</strong>:</p>
            <div className="space-y-2">
              {[
                ['view-distance=6', 'Reduce from default 10. Biggest performance win. Players see 6 chunks instead of 10.'],
                ['simulation-distance=4', 'How far away mobs and redstone simulate. Lower = faster.'],
                ['max-players=20', 'Cap players to match your RAM. 1GB per ~10 players on Paper.'],
                ['network-compression-threshold=256', 'Reduces bandwidth. Keep at 256.'],
              ].map(([setting, desc]) => (
                <div key={setting} className="bg-gray-800/40 rounded-xl px-4 py-3">
                  <code className="text-green-400 font-mono text-xs block mb-1">{setting}</code>
                  <span className="text-gray-400 text-xs">{desc}</span>
                </div>
              ))}
            </div>

            <h3 className="text-white font-bold mb-3 mt-6">Performance plugins</h3>
            <div className="space-y-2 text-sm">
              {[
                ['Spark', 'Profile your server to find exactly what\'s causing lag', 'https://spark.lucko.me'],
                ['ClearLag', 'Auto-removes dropped items and excess entities', 'https://www.spigotmc.org/resources/clearlagg.68271/'],
                ['FarmControl', 'Limits lag from large farms', 'https://www.spigotmc.org/resources/farmcontrol.86923/'],
              ].map(([name, desc, link]) => (
                <a key={name} href={link} target="_blank" rel="noreferrer" className="flex items-start gap-3 bg-gray-800/40 hover:bg-gray-800 rounded-xl px-4 py-3 transition-colors group">
                  <Zap className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white group-hover:text-green-400 transition-colors">{name}</span>
                    <span className="text-gray-400 text-xs block mt-0.5">{desc}</span>
                  </div>
                </a>
              ))}
            </div>

            <Callout type="success">Paper alone gives a 30–50% performance improvement over Vanilla. If you're on Vanilla and experiencing lag, switching to Paper is the single biggest improvement you can make.</Callout>
          </div>
        ),
      },
    ],
  },
];

// ─── MAIN DOCS PAGE ───────────────────────────────────────────
const DocsPage = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [openCategories, setOpenCategories] = useState({});

  // Search across all articles
  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    const results = [];
    DOCS.forEach(cat => {
      cat.articles.forEach(article => {
        if (
          article.title.toLowerCase().includes(q) ||
          article.tags.some(t => t.includes(q)) ||
          cat.title.toLowerCase().includes(q)
        ) {
          results.push({ ...article, category: cat.title, categoryId: cat.id, categoryIcon: cat.icon, categoryColor: cat.color });
        }
      });
    });
    return results;
  }, [search]);

  const openArticle = (catId, articleId) => {
    setActiveCategory(catId);
    setActiveArticle(articleId);
    setSearch('');
  };

  const currentCat = DOCS.find(c => c.id === activeCategory);
  const currentArticle = currentCat?.articles.find(a => a.id === activeArticle);

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-16 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-500/10 rounded-2xl mb-4">
            <BookOpen className="w-7 h-7 text-green-400" />
          </div>
          <h1 className="text-4xl font-black text-white mb-2">Documentation</h1>
          <p className="text-gray-400 text-lg">Everything you need to run a great Minecraft server</p>
        </div>

        {/* Search */}
        <div className="relative max-w-xl mx-auto mb-12">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search docs... (plugins, version, whitelist...)"
            className="w-full bg-gray-900 border border-gray-700 focus:border-green-500 rounded-2xl pl-12 pr-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors text-sm"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-lg">×</button>
          )}
        </div>

        {/* Search results */}
        {search && (
          <div className="max-w-xl mx-auto mb-8">
            {searchResults.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No results for "{search}"</p>
            ) : (
              <div className="space-y-2">
                {searchResults.map((r) => {
                  const Icon = r.categoryIcon;
                  return (
                    <button
                      key={`${r.categoryId}-${r.id}`}
                      onClick={() => openArticle(r.categoryId, r.id)}
                      className="w-full flex items-center gap-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-4 py-3 text-left transition-colors group"
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${r.categoryColor}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white text-sm">{r.title}</div>
                        <div className="text-gray-500 text-xs">{r.category}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!search && (
          <div className="flex gap-8">
            {/* Sidebar */}
            <div className="w-64 flex-shrink-0 hidden lg:block">
              <div className="sticky top-24 space-y-1">
                {DOCS.map((cat) => {
                  const Icon = cat.icon;
                  const isOpen = openCategories[cat.id] !== false;
                  const isCatActive = activeCategory === cat.id;
                  return (
                    <div key={cat.id}>
                      <button
                        onClick={() => {
                          setOpenCategories(p => ({ ...p, [cat.id]: !isOpen }));
                          if (!isCatActive) { setActiveCategory(cat.id); setActiveArticle(cat.articles[0].id); }
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isCatActive ? 'bg-green-500/10 text-green-400' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{cat.title}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                      </button>
                      {isOpen && (
                        <div className="ml-6 mt-0.5 space-y-0.5 mb-1">
                          {cat.articles.map((art) => (
                            <button
                              key={art.id}
                              onClick={() => { setActiveCategory(cat.id); setActiveArticle(art.id); }}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${activeArticle === art.id && activeCategory === cat.id ? 'text-green-400 bg-green-500/5' : 'text-gray-500 hover:text-gray-300'}`}
                            >
                              {art.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {!activeCategory ? (
                /* Category grid */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {DOCS.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => { setActiveCategory(cat.id); setActiveArticle(cat.articles[0].id); }}
                        className="bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-2xl p-6 text-left transition-all group hover:shadow-lg"
                      >
                        <div className={`w-11 h-11 ${cat.bg} rounded-xl flex items-center justify-center mb-4`}>
                          <Icon className={`w-5 h-5 ${cat.color}`} />
                        </div>
                        <h3 className="font-bold text-white text-lg mb-1 group-hover:text-green-400 transition-colors">{cat.title}</h3>
                        <p className="text-gray-500 text-sm mb-3">{cat.desc}</p>
                        <div className="text-xs text-gray-600">{cat.articles.length} article{cat.articles.length !== 1 ? 's' : ''}</div>
                      </button>
                    );
                  })}
                </div>
              ) : currentArticle ? (
                /* Article view */
                <div>
                  {/* Breadcrumb */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                    <button onClick={() => { setActiveCategory(null); setActiveArticle(null); }} className="hover:text-white transition-colors">Docs</button>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <button onClick={() => setActiveArticle(currentCat.articles[0].id)} className="hover:text-white transition-colors">{currentCat.title}</button>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="text-gray-300">{currentArticle.title}</span>
                  </div>

                  {/* Article */}
                  <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                    <h1 className="text-2xl font-black text-white mb-6">{currentArticle.title}</h1>
                    {currentArticle.content}
                  </div>

                  {/* Article nav */}
                  <div className="flex justify-between mt-4 gap-4">
                    {(() => {
                      const arts = currentCat.articles;
                      const idx = arts.findIndex(a => a.id === activeArticle);
                      const prev = arts[idx - 1];
                      const next = arts[idx + 1];
                      return (
                        <>
                          {prev ? (
                            <button onClick={() => setActiveArticle(prev.id)} className="flex items-center gap-2 bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl px-4 py-3 text-sm text-gray-400 hover:text-white transition-colors">
                              ← {prev.title}
                            </button>
                          ) : <div />}
                          {next && (
                            <button onClick={() => setActiveArticle(next.id)} className="flex items-center gap-2 bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl px-4 py-3 text-sm text-gray-400 hover:text-white transition-colors ml-auto">
                              {next.title} →
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Other articles in category */}
                  {currentCat.articles.length > 1 && (
                    <div className="mt-8">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">More in {currentCat.title}</h3>
                      <div className="space-y-2">
                        {currentCat.articles.filter(a => a.id !== activeArticle).map(a => (
                          <button key={a.id} onClick={() => setActiveArticle(a.id)} className="w-full flex items-center justify-between bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-4 py-3 text-left transition-colors group">
                            <span className="text-sm text-gray-300 group-hover:text-white">{a.title}</span>
                            <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Need more help */}
        {!search && (
          <div className="mt-16 bg-gradient-to-r from-green-500/5 to-blue-500/5 border border-green-500/10 rounded-2xl p-8 text-center">
            <h2 className="text-xl font-black text-white mb-2">Still need help?</h2>
            <p className="text-gray-400 mb-6">Our support team replies within 2 hours on average.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/tickets" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-6 py-3 rounded-xl text-sm transition-colors">
                Open a Support Ticket
              </Link>
              <a href="https://discord.gg/plarixmc" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-xl text-sm transition-colors">
                Join our Discord
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocsPage;
